import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import StickyBox from 'react-sticky-box';
import axios from 'axios';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import { Api } from '../../auth/Configs';
import { importTaskStates, taskDate } from './importTaskHelpers';
import './ArchiveStyles.css';
import './IncompleteImportTasks.css';

const endpoint = `${Api}/ged/documents/import-zip/tasks`;
const initialFilters = { search: '', state: '', from: '', to: '', view: 'incomplete', page: 1 };

function TaskProgress({ label, value, count, percent }) {
  return <div className="import-task-list-progress">
    <span>{value || 0}/{count} {label} <small>({percent || 0} %)</small></span>
    <progress aria-label={`${label} : ${value || 0} sur ${count}`} value={value || 0} max={count || 1} />
  </div>;
}

export default function IncompleteImportTasks() {
  const session = useSelector((state) => state.sessionAuth);
  const canImport = (session.roles || []).some((role) => ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role));
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${session.token}` } }), [session.token]);
  const [filters, setFilters] = useState(initialFilters);
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], total: 0, page: 1, perPage: 20 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [title, setTitle] = useState('');
  const [deleting, setDeleting] = useState(null);
  const requestNumber = useRef(0);
  const latestFilters = useRef(filters);
  latestFilters.current = filters;

  const load = useCallback(async (isCurrent = () => true) => {
    if (!canImport) { setLoading(false); return; }
    if (JSON.stringify(filters) !== JSON.stringify(latestFilters.current)) return;
    const request = ++requestNumber.current;
    const active = () => isCurrent() && request === requestNumber.current;
    try {
      const response = await axios.get(endpoint, { ...auth, params: filters });
      if (active()) {
        setData(response.data); setError('');
        if (response.data.page !== filters.page) setFilters((previous) => ({ ...previous, page: response.data.page }));
      }
    } catch (err) {
      if (active()) setError(err.response?.data?.message || 'Impossible de charger les lots sauvegardés.');
    } finally { if (active()) setLoading(false); }
  }, [auth, filters, canImport]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    load(() => active);
    return () => { active = false; };
  }, [load]);
  useEffect(() => {
    let active = true;
    const timer = setInterval(() => { if (!document.hidden && !busy && !renaming && !deleting) load(() => active); }, 10000);
    return () => { active = false; clearInterval(timer); };
  }, [load, busy, renaming, deleting]);

  const changeFilter = (key, value) => setFilters((previous) => ({ ...previous, [key]: value, page: 1 }));
  const action = async (row, name, extra = {}) => {
    setBusy(row.preparationId);
    try {
      await axios.post(`${endpoint}/${row.preparationId}/${name}`, { editRevision: row.editRevision, ...extra }, auth);
      setRenaming(null);
      await load();
    } catch (err) { setError(err.response?.data?.message || 'Action impossible. Actualisez le lot avant de réessayer.'); }
    finally { setBusy(''); }
  };
  const remove = async (row) => {
    setBusy(row.preparationId);
    try {
      await axios.delete(`${Api}/ged/documents/import-zip/preview/${row.preparationId}`, auth);
      setDeleting(null);
      if (data.items.length === 1 && filters.page > 1) setFilters((previous) => ({ ...previous, page: previous.page - 1 }));
      else await load();
    } catch (err) { setError(err.response?.data?.message || 'Suppression impossible.'); }
    finally { setBusy(''); }
  };
  const pages = Math.max(1, Math.ceil(data.total / data.perPage));

  return <>
    <Breadcrumb title="Suivi & contrôle / Tâches incomplètes" />
    <div className="content"><div className="container-fluid"><div className="row">
      <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar"><StickyBox offsetTop={20} offsetBottom={20}><Sidebar /></StickyBox></div>
      <main id="import-tasks" className="col-md-8 col-lg-9 col-xl-9 import-tasks-page">
        <header className="import-tasks-header">
          <div><h4 className="htitle">Tâches incomplètes</h4><p>Retrouvez vos lots sauvegardés et reprenez leur traitement à tout moment.</p></div>
          {canImport && <Link className="btn btn-primary" to="/apps/in/archives/import"><i className="ri-add-line" aria-hidden="true" /> Nouveau lot</Link>}
        </header>
        {!canImport ? <div className="alert alert-warning">Vous n’avez pas les droits pour traiter des lots d’import.</div> : <>
          <div className="import-tasks-tabs" role="group" aria-label="Lots affichés">
            <button type="button" aria-pressed={filters.view === 'incomplete'} className={`btn ${filters.view === 'incomplete' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFilter('view', 'incomplete')}>En cours de traitement</button>
            <button type="button" aria-pressed={filters.view === 'history'} className={`btn ${filters.view === 'history' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilters((previous) => ({ ...previous, state: '', view: 'history', page: 1 }))}>Historique des lots terminés</button>
          </div>
          <form className="import-tasks-filters" onSubmit={(event) => { event.preventDefault(); changeFilter('search', search); }}>
            <label>Rechercher un lot<input className="form-control" value={search} placeholder="Intitulé ou nom du ZIP" onChange={(event) => setSearch(event.target.value)} /></label>
            <label>État<select className="form-control" value={filters.state} onChange={(event) => changeFilter('state', event.target.value)} disabled={filters.view === 'history'}>
              <option value="">Tous les états</option>{Object.entries(importTaskStates).filter(([key]) => key !== 'completed').map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select></label>
            <label>Démarré à partir du<input type="date" className="form-control" value={filters.from} onChange={(event) => changeFilter('from', event.target.value)} /></label>
            <label>Jusqu’au<input type="date" className="form-control" value={filters.to} onChange={(event) => changeFilter('to', event.target.value)} /></label>
            <button className="btn btn-outline-primary" type="submit">Rechercher</button>
          </form>
          {error && <div role="alert" className="alert alert-danger">{error}</div>}
          <div className="import-tasks-summary"><span>{data.total} lot(s) {filters.view === 'history' ? 'terminé(s)' : 'en attente ou en cours'}</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => load()} disabled={loading}>Actualiser</button></div>
          {loading ? <p role="status">Chargement des lots…</p> : data.items.length === 0 ? <div className="import-tasks-empty">
            <i className="ri-inbox-line" aria-hidden="true" /><h5>{filters.view === 'history' ? 'Aucun lot terminé' : 'Aucune tâche incomplète'}</h5>
            <p>Les lots apparaissent ici dès le dépôt du ZIP. Ils restent sauvegardés sans expiration.</p>
          </div> : <div className="table-responsive"><table className="table syidocs-data-table import-tasks-table">
            <thead><tr><th>Lot</th><th>Date de démarrage</th><th>Fichiers</th><th>Progression</th><th>État</th><th>Dernière modification</th><th>Actions</th></tr></thead>
            <tbody>{data.items.map((row) => <React.Fragment key={row.preparationId}><tr>
              <td><Link to={`/apps/in/archives/import?preparationId=${row.preparationId}`}><strong>{row.title}</strong></Link><small className="d-block text-muted">{row.originalFilename}</small>
                {renaming?.preparationId === row.preparationId && <form className="import-task-rename" onSubmit={(event) => { event.preventDefault(); action(row, 'rename', { title }); }}>
                  <input aria-label="Nouvel intitulé du lot" className="form-control form-control-sm" maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} required />
                  <button type="submit" className="btn btn-sm btn-primary" disabled={Boolean(busy)}>Enregistrer</button><button type="button" className="btn btn-sm" onClick={() => setRenaming(null)}>Annuler</button>
                </form>}
              </td>
              <td className="text-nowrap">{taskDate(row.startedAt)}</td><td>{row.count}</td>
              <td><TaskProgress label="analysés" value={row.progress.analysisDone} count={row.count} percent={row.progress.analysisPercent} />
                <TaskProgress label="vérifiés" value={row.progress.verified} count={row.count} percent={row.progress.verifiedPercent} />
                {row.progress.imported > 0 && <small>{row.progress.imported}/{row.count} déjà importés</small>}</td>
              <td><span className={`import-task-badge state-${row.state}`}>{importTaskStates[row.state] || row.state}</span></td>
              <td className="text-nowrap">{taskDate(row.updatedAt)}</td>
              <td><div className="import-task-row-actions">
                <Link className="btn btn-sm btn-outline-primary" to={`/apps/in/archives/import?preparationId=${row.preparationId}`}>{row.status === 'imported' ? 'Consulter' : 'Reprendre'}</Link>
                {row.status !== 'importing' && <button type="button" className="btn btn-sm" disabled={Boolean(busy)} onClick={() => { setRenaming(row); setTitle(row.title); }}>Renommer</button>}
                {['preparing', 'paused'].includes(row.status) && <button type="button" className="btn btn-sm" disabled={Boolean(busy)} onClick={() => action(row, row.status === 'paused' ? 'resume' : 'pause')}>{row.status === 'paused' ? 'Reprendre l’analyse' : 'Pause'}</button>}
                {row.status !== 'importing' && <button type="button" className="btn btn-sm btn-outline-danger" disabled={Boolean(busy)} onClick={() => setDeleting(row)}>Supprimer</button>}
              </div></td>
            </tr>{deleting?.preparationId === row.preparationId && <tr><td colSpan={7}><div className="alert alert-warning mb-0" role="alert">
              Supprimer le lot « {row.title} » et ses sources de travail ? Les archives déjà importées seront conservées.
              <div className="mt-2"><button type="button" className="btn btn-sm btn-danger" disabled={Boolean(busy)} onClick={() => remove(row)}>Supprimer définitivement le lot</button>
                <button type="button" className="btn btn-sm" onClick={() => setDeleting(null)}>Annuler</button></div>
            </div></td></tr>}</React.Fragment>)}</tbody>
          </table></div>}
          <div className="import-tasks-pagination"><button type="button" className="btn btn-sm btn-outline-secondary" disabled={filters.page <= 1 || loading} onClick={() => setFilters((previous) => ({ ...previous, page: previous.page - 1 }))}>Précédent</button>
            <span>Page {filters.page} / {pages}</span><button type="button" className="btn btn-sm btn-outline-secondary" disabled={filters.page >= pages || loading} onClick={() => setFilters((previous) => ({ ...previous, page: previous.page + 1 }))}>Suivant</button></div>
        </>}
      </main>
    </div></div></div>
  </>;
}
